import {
  type CostModel,
  type Device,
  FeaturedAds,
  Placement,
} from '@hc-frontend/core-types';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { getDeviceByViewportWidth } from '@hc-frontend/core-utils-device';
import {
  trackClick,
  TrackClickRequestDto,
  trackImpression,
  TrackImpressionRequestDto,
} from '@hc-frontend/services';

import { AdImpression } from '../../domain';
import type { SemContentRepository } from '../../domain/repositories/sem-content-repository';

type SemContentAdapterOptions = {
  baseUrl: string;
};

function parseDevice<
  T extends
    | TrackClickRequestDto['device']
    | TrackImpressionRequestDto['device'],
>(device: Device) {
  return `${device.charAt(0).toUpperCase()}${device.toLowerCase().slice(1)}` as T;
}

function addActionBlock(
  blocks: TrackImpressionRequestDto['blocks'],
): TrackImpressionRequestDto['blocks'] {
  const actionsBlock: TrackImpressionRequestDto['blocks'][number] = {
    name: 'action_steps',
    position: `${blocks.length + 1}`,
    size: '0',
    max_cpc: '0',
    ctr: '0',
    expected_revenue: '0',
    layout: 'vertical',
  };
  return [...blocks, actionsBlock];
}

function buildBlocks(
  result: FeaturedAds['result'],
): TrackImpressionRequestDto['blocks'] {
  const maxCPC = result.reduce(
    (value, ad) => (value > ad.cpc ? value : ad.cpc),
    0,
  );

  return [
    {
      ctr: '0',
      expected_revenue: '0',
      layout: 'vertical',
      max_cpc: maxCPC.toString(),
      name: 'featuredAds',
      position: '1',
      size: result.length.toString(),
    },
  ];
}

export function semContentAdapterFactory(
  options: SemContentAdapterOptions,
): SemContentRepository {
  return {
    trackClick: async (parameters) => {
      const {
        featureAd,
        searchAdsId,
        zipCode,
        sessionId,
        stateAcronym,
        placement,
        productType,
        blockPosition,
        pagePosition,
        impressionId,
        clickCount,
        device,
      } = parameters;

      const payload: TrackClickRequestDto = {
        ad_id: featureAd.id,
        ad_impression_id: searchAdsId,
        block: 'featureAds',
        block_position: 1,
        brand_name: featureAd.provider.name,
        click_url: featureAd.link,
        cpc: featureAd.cpc.toString(),
        deal_type: featureAd.dealType.toLowerCase() as Lowercase<CostModel>,
        display_url: featureAd.provider.displayUrl,
        is_above_fold: 'false', // validate
        metal_level: undefined,
        position_in_block: blockPosition.toString(),
        position_in_page: pagePosition.toString(),
        premium: '',
        click_count: clickCount,
        complete_url: window.location.href, // pending
        device: device
          ? parseDevice(device)
          : parseDevice(getDeviceByViewportWidth()),
        impression_id: impressionId,
        placement,
        product: productType,
        ranking_type: 'none',
        repeated: clickCount > 1 ? 'true' : 'false',
        session_id: sessionId,
        state: stateAcronym,
        zipcode: zipCode,
      };

      try {
        const result = await trackClick(options.baseUrl, payload);
        if (result.result === 'success') {
          return createSuccessOperationResult(undefined);
        }

        throw new Error(`Error tracking click of ad with id ${featureAd.id}`);
      } catch (error) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToTrackClick',
              rootCause: error as Error,
            },
          ],
        });
      }
    },
    trackImpression: async ({
      featureAds,
      stateAcronym,
      sessionId,
      zipCode,
      placement,
      device,
      productType,
      domain,
    }) => {
      const parsedAds: TrackImpressionRequestDto['ads'] = featureAds.result.map(
        (ad, index) => ({
          ad_id: ad.id,
          ad_impression_id: featureAds.id,
          block: 'featureAds',
          block_position: 1,
          brand_name: ad.provider.name,
          click_url: ad.link,
          cpc: ad.cpc.toString(),
          deal_type: ad.dealType.toLowerCase() as Lowercase<CostModel>,
          display_url: ad.provider.displayUrl,
          is_above_fold: 'true',
          position_in_block: `${index + 1}`,
          position_in_page: `${index + 1}`,
          metal_level: undefined,
        }),
      );

      let blocks = buildBlocks(featureAds.result);

      blocks =
        placement === Placement.enum.TYP ? addActionBlock(blocks) : blocks;

      const payload: TrackImpressionRequestDto = {
        blocks,
        ads: parsedAds,
        state: stateAcronym,
        zipcode: zipCode,
        placement,
        session_id: sessionId,
        ranking_type: 'none',
        impression_id: 1 * Date.now(),
        product: productType,
        complete_url: window.location.href,
        domain_short_name: `${productType}_${domain}`,
        lead_product: '2.0',
        device: device
          ? parseDevice(device)
          : parseDevice(getDeviceByViewportWidth()),
      };
      try {
        const response = await trackImpression(options.baseUrl, payload);

        if (response.result === 'success' && response.body !== undefined) {
          return createSuccessOperationResult(
            AdImpression.parse(response.body.data.impression_id),
          );
        }

        throw new Error(
          `Error tracking impression of searchad result with id ${featureAds.id}`,
        );
      } catch (error) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToTrackImpression',
              rootCause: error as Error,
            },
          ],
        });
      }
    },
  };
}
